import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, Users, CreditCard, AlertCircle, Clock3, Activity } from 'lucide-react';
import { fadeInUp, staggerContainer, prefersReducedMotion, useTypingAnimation } from '../lib/visualEffects';
import { useData } from '../providers/DataProvider';
import { computeOrderCashNeeded, computeOrderChargeBreakdown, computeSaleChargeBreakdown } from '../lib/orderCharges';

const timeframeOptions = [
  { id: 'today', label: 'Hoy', days: 1 },
  { id: '7', label: 'Últimos 7 días', days: 7 },
  { id: '14', label: 'Últimos 14 días', days: 14 },
  { id: '30', label: 'Últimos 30 días', days: 30 },
  { id: '90', label: 'Últimos 90 días', days: 90 },
  { id: '365', label: 'Últimos 12 meses', days: 365 },
  { id: 'custom', label: 'Personalizado…', days: null, custom: true },
  { id: 'all', label: 'Todo el periodo', days: null },
];

const Dashboard = ({ transactions = [], branches = [], currentUser = {} }) => {
  const [timeframe, setTimeframe] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const reduceMotion = useMemo(() => prefersReducedMotion(), []);
  const welcomeCopy = useTypingAnimation('Aquí tienes un resumen actualizado de tu operación.', 28);
  const data = (typeof useData === 'function') ? useData() : null;
  const branchBalances = Array.isArray(data?.branchBalances) ? data.branchBalances : [];
  const branchMovements = Array.isArray(data?.branchMovements) ? data.branchMovements : [];
  const branchProfits = Array.isArray(data?.branchProfits) ? data.branchProfits : [];
  const profitCenters = Array.isArray(data?.profitCenters) ? data.profitCenters : [];
  const transactionProfits = Array.isArray(data?.transactionProfits) ? data.transactionProfits : [];
  const monipaySummary = useMemo(() => {
    return profitCenters.find((pc) => String(pc.code || '').toUpperCase() === 'MONIPAY') || null;
  }, [profitCenters]);
  const clients = Array.isArray(data?.clients) ? data.clients : [];
  const suppliers = Array.isArray(data?.suppliers) ? data.suppliers : [];
  const allBranches = Array.isArray(data?.branches) ? data.branches : branches;

  const parseDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  // Robust amount parser: handles numbers and strings like "1.000.000" or "1,000,000" or "1000000.50"
  const parseAmount = (v) => {
    if (v == null || v === '') return 0;
    if (typeof v === 'number') {
      return Number.isFinite(v) ? v : 0;
    }
    if (typeof v === 'string') {
      const s = v.trim();
      if (s === '') return 0;
      // Keep digits and separators to detect pattern
      const only = s.replace(/[^0-9.,\-]/g, '');
      const hasComma = only.includes(',');
      const hasDot = only.includes('.');
      let normalized = only;
      if (hasComma && hasDot) {
        // Assume comma is decimal, dot is thousands
        normalized = only.replace(/\./g, '').replace(/,/g, '.');
      } else if (hasComma && !hasDot) {
        // Treat comma as decimal
        normalized = only.replace(/,/g, '.');
      } else {
        // Remove thousand separators if any stray spaces
        normalized = only;
      }
      const n = Number(normalized);
      return Number.isFinite(n) ? n : 0;
    }
    // Fallback
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const now = useMemo(() => new Date(), []);

  const selectedOption = timeframeOptions.find((option) => option.id === timeframe) || timeframeOptions[0];

  const toStartOfDay = (d) => { const nd = new Date(d); nd.setHours(0,0,0,0); return nd; };
  const toEndOfDay = (d) => { const nd = new Date(d); nd.setHours(23,59,59,999); return nd; };

  const { rangeStart, rangeEnd, prevRangeStart, prevRangeEnd } = useMemo(() => {
    // Compute current range
    let end = toEndOfDay(new Date());
    let start = null;
    if (selectedOption.custom && customStart && customEnd) {
      start = toStartOfDay(new Date(customStart));
      end = toEndOfDay(new Date(customEnd));
    } else if (selectedOption.id === 'all' || selectedOption.days == null) {
      start = null; // no restriction
    } else if (selectedOption.id === 'today') {
      const d = new Date();
      start = toStartOfDay(d);
      end = toEndOfDay(d);
    } else if (typeof selectedOption.days === 'number') {
      start = new Date(end);
      start.setDate(start.getDate() - selectedOption.days);
    }

    // Compute previous range for comparisons (same length and immediately preceding)
    let prevStart = null;
    let prevEnd = null;
    if (start && end) {
      const lenMs = end.getTime() - start.getTime();
      prevEnd = new Date(start.getTime());
      prevEnd.setMilliseconds(prevEnd.getMilliseconds() - 1);
      prevStart = new Date(prevEnd.getTime() - lenMs);
    }

    return { rangeStart: start, rangeEnd: end, prevRangeStart: prevStart, prevRangeEnd: prevEnd };
  }, [selectedOption, customStart, customEnd]);

  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) return [];
    const withinWindow = transactions.filter((transaction) => {
      if (!rangeStart || !rangeEnd) return true;
      const date = parseDate(transaction.date);
      if (!date) return true;
      return date >= rangeStart && date <= rangeEnd;
    });
    // Exclude cancelled from default dashboard views to avoid confusion
    const nonCancelled = withinWindow.filter((t) => String(t.status) !== 'cancelled');
    // Sort by date desc (fallback to created_at) so "recent" truly means most recent
    return nonCancelled.sort((a, b) => {
      const da = parseDate(a.date) || parseDate(a.created_at) || new Date(0);
      const db = parseDate(b.date) || parseDate(b.created_at) || new Date(0);
      return db.getTime() - da.getTime();
    });
  }, [transactions, rangeStart, rangeEnd, now]);

  const previousTransactions = useMemo(() => {
    if (!Array.isArray(transactions) || !prevRangeStart || !prevRangeEnd) return [];
    return transactions.filter((transaction) => {
      const date = parseDate(transaction.date);
      if (!date) return false;
      return date >= prevRangeStart && date <= prevRangeEnd;
    });
  }, [transactions, prevRangeStart, prevRangeEnd]);

  const sumAmounts = (txs, selector) => txs.reduce((sum, tx) => sum + parseAmount(selector(tx)), 0);

  // Internal operations removed: include all sales/orders (no internal filtering)
  const currentSales = useMemo(() => sumAmounts(filteredTransactions.filter((t) => t.type === 'sale'), (t) => t.amount), [filteredTransactions]);
  const previousSales = useMemo(() => sumAmounts(previousTransactions.filter((t) => t.type === 'sale'), (t) => t.amount), [previousTransactions]);

  const calcSaleCommission = (t) => {
    const baseAmount = parseAmount(t.amount);
    const breakdown = computeSaleChargeBreakdown(t, { baseAmount }) || {};

    const base = Number(breakdown.base || baseAmount) || 0;
    const external = Number(breakdown.external || 0) || 0;
    let internal = Number(breakdown.internal || 0) || 0;

    if (!internal) {
      const totalToCollect = Number(breakdown.totalToCollect || 0) || 0;
      if (totalToCollect && base) {
        const implied = totalToCollect - base - external;
        if (implied > 0) internal = implied;
      }
    }

    if (!internal) {
      const salePrice = Number(t.salePrice ?? t.sale_price ?? 0) || 0;
      if (salePrice && base) {
        const implied = salePrice - base - external;
        if (implied > 0) internal = implied;
      }
    }

    if (!internal) {
      const fallback = Number(t.profit ?? t.metadata?.profit ?? 0) || 0;
      if (fallback) internal = fallback;
    }

    if (!internal && baseAmount) {
      const legacyPctRaw = Number(t.salePercentage ?? t.sale_percentage ?? 0) || 0;
      if (legacyPctRaw) {
        const pct = legacyPctRaw <= 1 ? legacyPctRaw : legacyPctRaw / 100;
        const computed = baseAmount * pct;
        if (computed > 0) internal = computed;
      }
    }

    return Math.round(internal * 100) / 100;
  };

  const isOpenOrder = (t) => {
    if (!t || (t.type || t.transaction_type) !== 'order') return false;
    const s = (t.status || '').toString();
    return s === 'pending_deposit' || s === 'pending_delivery' || s === 'pending';
  };
  const currentOrders = useMemo(() => filteredTransactions.filter(isOpenOrder).length, [filteredTransactions]);
  const previousOrders = useMemo(() => previousTransactions.filter(isOpenOrder).length, [previousTransactions]);

  const calcDelta = (currentValue, previousValue) => {
    if (previousValue === 0 || previousValue === null || previousValue === undefined) {
      if (!currentValue) return { delta: 0, trend: 'neutral' };
      return { delta: 100, trend: 'up' };
    }
    const raw = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
    if (Number.isNaN(raw) || !Number.isFinite(raw)) return { delta: 0, trend: 'neutral' };
    if (raw > 0) return { delta: raw, trend: 'up' };
    if (raw < 0) return { delta: raw, trend: 'down' };
    return { delta: 0, trend: 'neutral' };
  };

  const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  // Balances por sucursal: vista + fallback por transacciones
  const getBranchKey = (item) => String(item?.branchId ?? item?.branch_id ?? '');

  const computeTxTotalsForBranch = (branchId) => {
    try {
      const txs = Array.isArray(transactions) ? transactions : [];
      const branchKey = String(branchId ?? '');
      if (!branchKey) return { entradas: 0, salidas: 0 };
      let entradas = 0;
      let salidas = 0;
      txs.forEach((t) => {
        const status = (t.status || '').toString();
        if (status === 'cancelled') return;
        const type = (t.type || t.transaction_type || '').toString();
        const sourceKey = String(t.sourceBranchId ?? t.source_branch_id ?? t.branchId ?? t.branch_id ?? '');
        const targetKey = String(t.targetBranchId ?? t.target_branch_id ?? t.metadata?.transfer?.targetBranchId ?? t.metadata?.targetBranchId ?? '');

        if (type === 'order') {
          if (sourceKey === branchKey) {
            const cashNeeded = computeOrderCashNeeded(t, { baseAmount: parseAmount(t.amount) });
            const delta = Number.isFinite(cashNeeded) ? cashNeeded : parseAmount(t.amount);
            entradas += delta;
          }
          return;
        }

        if (type === 'transfer') {
          // Transfer moves base amount from source to target
          const base = parseAmount(t.amount);
          if (sourceKey === branchKey) {
            salidas += base;
          }
          if (targetKey && targetKey === branchKey) {
            entradas += base;
          }
          return;
        }

        if (type === 'sale') {
          const delivered = status === 'delivered' || status === 'completed' || status === 'paid';
          if (delivered && sourceKey === branchKey) {
            salidas += parseAmount(t.amount);
          }
        }
      });
      return { entradas, salidas };
    } catch {
      return { entradas: 0, salidas: 0 };
    }
  };
  const getBranchBalanceRow = (branchId) => {
    if (!branchId) return null;
    const key = String(branchId);
    const row = branchBalances.find((r) => {
      const rid = String(r.branch_id ?? r.id ?? r.branchId ?? '');
      return rid === key;
    });
    return row || null;
  };
  const computeMovementTotalsForBranch = (branchId) => {
    try {
      const ms = Array.isArray(branchMovements) ? branchMovements : [];
      const filtered = ms.filter((m) => String(m.branch_id) === String(branchId));
      const entradas = filtered.reduce((sum, m) => sum + (m.movement_type === 'entrada' ? Number(m.amount || 0) : 0), 0);
      const salidas = filtered.reduce((sum, m) => sum + (m.movement_type === 'salida' ? Number(m.amount || 0) : 0), 0);
      return { entradas, salidas };
    } catch {
      return { entradas: 0, salidas: 0 };
    }
  };
  const getBranchCash = (branch) => {
    // Compute expected from local transactions/movements
    const { entradas, salidas } = computeTxTotalsForBranch(branch?.id);
    const mov = computeMovementTotalsForBranch(branch?.id);
    const baseline = Number((branch && (branch.initialCash != null ? branch.initialCash : branch.cashBalance)) || 0) || 0;
    const expected = baseline + entradas - salidas + (mov.entradas - mov.salidas);

    // Prefer DB view v_branch_balances when present, but reconcile against expected
    const row = getBranchBalanceRow(branch?.id);
    if (row) {
      const candidates = [row.cash_balance, row.balance, row.current_balance, row.cash, row.cashBalance];
      for (const v of candidates) {
        const n = Number(v);
        if (Number.isFinite(n)) {
          // If DB and computed differ significantly, prefer computed (expected)
          if (Math.abs(n - expected) > 0.5) return expected;
          return n;
        }
      }
    }
    // No authoritative value: return computed expected
    return expected;
  };
  const branchesByCash = useMemo(() => {
    return (allBranches || []).map(b => ({ id: b.id, name: b.name || '—', cash: getBranchCash(b) }))
      .sort((a, b) => b.cash - a.cash)
      .slice(0, 6);
  }, [allBranches, transactions, branchBalances, branchProfits]);

  const ordersForCosts = useMemo(() => filteredTransactions.filter((t) => (t.type || t.transaction_type) === 'order'), [filteredTransactions]);
  const ordersForCostsPrevious = useMemo(() => previousTransactions.filter((t) => (t.type || t.transaction_type) === 'order'), [previousTransactions]);

  const salesForEarnings = useMemo(() => filteredTransactions.filter((t) => (t.type || t.transaction_type) === 'sale'), [filteredTransactions]);
  const salesForEarningsPrevious = useMemo(() => previousTransactions.filter((t) => (t.type || t.transaction_type) === 'sale'), [previousTransactions]);

  // Calculate earnings using new profit data if available
  // Raw profits (sum of total_revenue) using raw summary when available
  const currentRawProfits = useMemo(() => {
    const src = Array.isArray(data?.transactionProfitsRaw) ? data.transactionProfitsRaw : [];
    if (src.length > 0) {
      const relevant = src.filter(tp => {
        const txDate = parseDate(tp.created_at);
        if (!rangeStart || !rangeEnd || !txDate) return true;
        return txDate >= rangeStart && txDate <= rangeEnd;
      });
      return relevant.reduce((sum, tp) => sum + Number(tp.total_revenue ?? 0), 0);
    }
    // Fallback: approximate using commissions on sales only
    return sumAmounts(salesForEarnings, calcSaleCommission);
  }, [data?.transactionProfitsRaw, salesForEarnings, rangeStart, rangeEnd, now]);

  const previousRawProfits = useMemo(() => {
    const src = Array.isArray(data?.transactionProfitsRaw) ? data.transactionProfitsRaw : [];
    if (src.length > 0 && prevRangeStart && prevRangeEnd) {
      const relevant = src.filter(tp => {
        const txDate = parseDate(tp.created_at);
        if (!txDate) return false;
        return txDate >= prevRangeStart && txDate <= prevRangeEnd;
      });
      return relevant.reduce((sum, tp) => sum + Number(tp.total_revenue ?? 0), 0);
    }
    return sumAmounts(salesForEarningsPrevious, calcSaleCommission);
  }, [data?.transactionProfitsRaw, salesForEarningsPrevious, prevRangeStart, prevRangeEnd]);

  const totalOrderCharges = useMemo(() => {
    // Use raw (non-WAC) summary for order costs to avoid zeroing under WAC
    const src = Array.isArray(data?.transactionProfitsRaw) && data.transactionProfitsRaw.length > 0
      ? data.transactionProfitsRaw
      : transactionProfits;
    if (src.length > 0) {
      const relevantProfits = src.filter(tp => {
        const txDate = parseDate(tp.created_at);
        if (!rangeStart || !rangeEnd || !txDate) return true;
        return txDate >= rangeStart && txDate <= rangeEnd;
      });
      return relevantProfits.reduce((sum, tp) => {
        if (tp.transaction_type === 'order') {
          // For orders, net_profit is negative (costs)
          return sum + Number(tp.net_profit ?? 0);
        }
        return sum;
      }, 0);
    }
    return sumAmounts(
      ordersForCosts,
      (t) => {
        const breakdown = computeOrderChargeBreakdown(t, { baseAmount: parseAmount(t.amount) });
        return (Number(breakdown.internal || 0) || 0) - (Number(breakdown.external || 0) || 0);
      }
    );
  }, [ordersForCosts, transactionProfits, data?.transactionProfitsRaw, rangeStart, rangeEnd, now]);

  const previousOrderCharges = useMemo(() => {
    const src = Array.isArray(data?.transactionProfitsRaw) && data.transactionProfitsRaw.length > 0
      ? data.transactionProfitsRaw
      : transactionProfits;
    if (src.length > 0 && prevRangeStart && prevRangeEnd) {
      const relevantProfits = src.filter(tp => {
        const txDate = parseDate(tp.created_at);
        if (!txDate) return false;
        return txDate >= prevRangeStart && txDate <= prevRangeEnd;
      });
      return relevantProfits.reduce((sum, tp) => {
        if (tp.transaction_type === 'order') {
          return sum + Number(tp.net_profit ?? 0);
        }
        return sum;
      }, 0);
    }
    return sumAmounts(
      ordersForCostsPrevious,
      (t) => {
        const breakdown = computeOrderChargeBreakdown(t, { baseAmount: parseAmount(t.amount) });
        return (Number(breakdown.internal || 0) || 0) - (Number(breakdown.external || 0) || 0);
      }
    );
  }, [ordersForCostsPrevious, transactionProfits, data?.transactionProfitsRaw, prevRangeStart, prevRangeEnd]);

  const currentNetEarnings = useMemo(() => {
    // Costs come in negative from raw summary → add them to profits
    return Number(currentRawProfits || 0) + Number(totalOrderCharges || 0);
  }, [currentRawProfits, totalOrderCharges]);

  const previousNetEarnings = useMemo(() => {
    return Number(previousRawProfits || 0) + Number(previousOrderCharges || 0);
  }, [previousRawProfits, previousOrderCharges]);

  // Métricas por sucursal: tarjetas individuales por sucursal con saldo > 0
  const branchCashList = useMemo(() => {
    return (allBranches || []).map(b => ({ id: b.id, name: b.name || '—', cash: getBranchCash(b) }))
      .filter(b => Number(b.cash) !== 0)
      .sort((a, b) => b.cash - a.cash);
  }, [allBranches, transactions, branchBalances, branchProfits]);

  const baseMetrics = [
    {
      label: 'Ventas Totales',
      value: formatCurrency(currentSales),
      delta: calcDelta(currentSales, previousSales),
      icon: DollarSign,
      color: 'blue',
    },
    {
      label: 'Ganancias',
      value: formatCurrency(currentRawProfits),
      delta: calcDelta(currentRawProfits, previousRawProfits),
      icon: TrendingUp,
      color: 'blue',
    },
    {
      label: 'Ganancia Neta',
      value: formatCurrency(currentNetEarnings),
      delta: calcDelta(currentNetEarnings, previousNetEarnings),
      icon: TrendingUp,
      color: 'green',
    },
    {
      label: 'Costos de pedidos',
      value: formatCurrency(totalOrderCharges),
      delta: calcDelta(totalOrderCharges, previousOrderCharges),
      icon: CreditCard,
      color: 'purple',
    },
    {
      label: 'Pedidos Abiertos',
      value: currentOrders,
      delta: calcDelta(currentOrders, previousOrders),
      icon: CreditCard,
      color: 'orange',
    },
  ];

  const branchMetrics = branchCashList.map(b => ({
    label: b.name,
    value: formatCurrency(b.cash),
    delta: { delta: 0, trend: 'neutral' },
    icon: DollarSign,
    color: 'orange',
  }));

  const metrics = [...baseMetrics];

  if (monipaySummary) {
    metrics.push({
      label: 'MoniPay acumulado',
      value: formatCurrency(Number(monipaySummary.net_profit || 0)),
      delta: { delta: 0, trend: 'neutral' },
      icon: Activity,
      color: 'emerald',
    });
  }

  const metricByLabel = (metricList, label) => metricList.find((metric) => metric.label === label) || null;

  const metricTrendCopy = (metric) => {
    if (!metric) return 'Sin datos suficientes para comparar.';
    const { delta, trend } = metric.delta;
  const formatted = Math.abs(delta).toLocaleString('en-US', { maximumFractionDigits: 1 });
    if (trend === 'up') return `Crecimiento de +${formatted}% frente al período anterior.`;
    if (trend === 'down') return `Caída de -${formatted}% frente al período anterior.`;
    return 'Sin variación frente al período anterior.';
  };

  const aggregateByBranch = (txs, branchList) => {
    if (!Array.isArray(txs) || txs.length === 0) return [];
    const totals = new Map();
    txs.forEach((tx) => {
      const branchId = getBranchKey(tx) || 'sin-branch';
      const amount = Number(tx.amount || 0);
      totals.set(branchId, (totals.get(branchId) || 0) + amount);
    });
    const totalAmount = Array.from(totals.values()).reduce((sum, value) => sum + value, 0) || 1;
    return Array.from(totals.entries())
      .map(([id, totalSales]) => {
  const branch = branchList.find((b) => String(b.id) === String(id)) || {};
        return {
          id,
          name: branch.name || 'Sin sucursal',
          totalSales,
          contribution: Math.min(100, Math.round((totalSales / totalAmount) * 100)),
        };
      })
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 5);
  };

  const totalCommittedOwnFunds = useMemo(() => {
    return transactions.reduce((sum, t) => {
      if (t.type === 'order' && t.fundSource === 'own' && t.status === 'pending') {
        const cashNeeded = computeOrderCashNeeded(t);
        const delta = Number.isFinite(cashNeeded) ? cashNeeded : Number(t.amount || 0) || 0;
        return sum + delta;
      }
      return sum;
    }, 0);
  }, [transactions]);

  const recentTransactions = useMemo(() => filteredTransactions.slice(0, 5), [filteredTransactions]);
  // Productivity widget removed

  

  // Mapear colores a clases compatibles con dark mode (evitar clases dinámicas no detectadas por Tailwind)
  const colorStyles = {
    blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-300' },
    green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-300' },
    purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-300' },
    orange: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-300' },
    emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-300' },
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">¡Bienvenido, {currentUser?.name || 'equipo MONIPAY'}!</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-300">{welcomeCopy || 'Aquí tienes un resumen actualizado de tu operación.'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-gray-300/80 bg-white px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <Clock3 className="h-4 w-4" />
              <span>{selectedOption.label}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {timeframeOptions.map((option) => (
                <motion.button
                  key={option.id}
                  type="button"
                  onClick={() => setTimeframe(option.id)}
                  whileHover={reduceMotion ? undefined : { y: -2 }}
                  whileTap={reduceMotion ? undefined : { scale: 0.96 }}
                  className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                    option.id === timeframe
                      ? 'border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
                      : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-blue-500 dark:hover:text-blue-200'
                  }`}
                >
                  {option.id === 'all' ? 'Todo' : option.id === 'today' ? 'Hoy' : option.id === 'custom' ? 'Personalizado' : option.label.replace('Últimos ', '')}
                </motion.button>
              ))}
            </div>
            {timeframe === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40"
                />
                <span className="text-gray-400">—</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      <motion.div
        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          const deltaValue = Math.abs(metric.delta.delta).toLocaleString('en-US', { maximumFractionDigits: 1 });
          const isPositive = metric.delta.trend === 'up';
          const isNegative = metric.delta.trend === 'down';
          return (
            <motion.div
              key={index}
              className="rounded-xl border border-gray-200 bg-white p-6 transition-all dark:border-gray-700 dark:bg-gray-800 neon-border"
              variants={fadeInUp}
              whileHover={reduceMotion ? undefined : { y: -4, scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 200, damping: 24 }}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {metric.label}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{metric.value}</p>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                        isPositive
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : isNegative
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      <Activity className="h-3.5 w-3.5" />
                      {`${isNegative ? '-' : '+'}${deltaValue}%`}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">vs período anterior</span>
                  </div>
                </div>
                <div className={`rounded-full p-3 ${colorStyles[metric.color].bg}`}>
                  <Icon className={`h-6 w-6 ${colorStyles[metric.color].text}`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
      
      {/* Balance por sucursal - full width widget for visibility */}
      <motion.div
  className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-8 neon-border"
        variants={fadeInUp}
        initial="initial"
        animate="animate"
        transition={{ duration: reduceMotion ? 0 : 0.35, delay: 0.1 }}
      >
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Balance por sucursal</h2>
        {branchCashList.length > 0 ? (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {branchCashList.map((b) => (
              <li key={b.id} className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">{b.name}</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(b.cash)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Sin sucursales con saldo distinto de cero.</p>
        )}
      </motion.div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
  <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 neon-border">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Operaciones Recientes</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Monto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cliente/Proveedor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Sucursal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {recentTransactions.map((transaction) => {
                  const type = (transaction.type || transaction.transaction_type || '').toString();
                  const isSale = type === 'sale';
                  const isOrder = type === 'order';
                  const isTransfer = type === 'transfer';
                  const sourceKey = String(transaction.sourceBranchId ?? transaction.source_branch_id ?? transaction.branchId ?? transaction.branch_id ?? '');
                  const targetKey = String(transaction.targetBranchId ?? transaction.target_branch_id ?? transaction.metadata?.transfer?.targetBranchId ?? transaction.metadata?.targetBranchId ?? '');
                  const branchName = isTransfer
                    ? `${(allBranches.find((b)=>String(b.id)===sourceKey)?.name || 'Origen')} → ${(allBranches.find((b)=>String(b.id)===targetKey)?.name || 'Destino')}`
                    : (allBranches.find((b) => String(b.id) === (getBranchKey(transaction)))?.name || 'N/A');
                  const clientName = clients.find(c => String(c.id) === String(transaction.clientId ?? transaction.client_id))?.company_name
                    || clients.find(c => String(c.id) === String(transaction.clientId ?? transaction.client_id))?.name
                    || null;
                  const supplierName = suppliers.find(s => String(s.id) === String(transaction.supplierId ?? transaction.supplier_id))?.name || null;
                  const partnerName = isSale
                    ? (clientName || 'Cliente sin asignar')
                    : isOrder
                      ? (supplierName || 'Proveedor sin asignar')
                      : '—';
                  const statusLabel = (() => {
                    const s = (transaction.status || '').toString();
                    switch (s) {
                      case 'pending_deposit': return 'Depósito pendiente';
                      case 'pending_delivery': return 'Entrega pendiente';
                      case 'delivered': return 'Entregado';
                      case 'cancelled': return 'Cancelado';
                      case 'completed': return 'Completado';
                      case 'paid': return 'Pagado';
                      default: return s || '—';
                    }
                  })();
                  return (
                    <tr key={transaction.id} className="hover:bg-gray-50 hover:dark:bg-gray-700/60">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{transaction.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          isSale
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : isTransfer
                              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {isSale ? 'Venta' : isTransfer ? 'Transferencia interna' : 'Pedido'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">${transaction.amount.toLocaleString('en-US')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{partnerName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{branchName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                            (transaction.status === 'pending' || transaction.status === 'pending_deposit' || transaction.status === 'pending_delivery')
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {recentTransactions.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No hay operaciones recientes. ¡Empieza a registrar algunas!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
  <div className="space-y-6">
          <motion.div
            className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 neon-border"
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={{ duration: reduceMotion ? 0 : 0.35, delay: 0.1 }}
          >
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Alertas Rápidas</h2>
            <motion.div className="space-y-4" variants={staggerContainer} initial="initial" animate="animate">
              <motion.div
                className="flex items-start rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/30"
                variants={fadeInUp}
              >
                <AlertCircle className="mr-3 h-5 w-5 text-red-500 dark:text-red-400" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">Fondos propios comprometidos</p>
                  <p className="text-xs text-red-700 dark:text-red-400">${totalCommittedOwnFunds.toLocaleString('en-US')} pendientes en pedidos financiados internamente.</p>
                </div>
              </motion.div>
              <motion.div
                className="flex items-start rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/30"
                variants={fadeInUp}
              >
                <Users className="mr-3 h-5 w-5 text-blue-500 dark:text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Clientes incorporados</p>
                  <p className="text-xs text-blue-700 dark:text-blue-400">—</p>
                </div>
              </motion.div>
              <motion.div
                className="flex items-start rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/30"
                variants={fadeInUp}
              >
                <TrendingUp className="mr-3 h-5 w-5 text-green-500 dark:text-green-400" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">Tendencia de ganancias</p>
                  <p className="text-xs text-green-700 dark:text-green-400">{metricTrendCopy(metricByLabel(metrics, 'Ganancia Neta'))}</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Productivity widget removed */}

          

          
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
